import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Tag,
  Text,
  Textarea,
  Tooltip,
  useToast,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { CalendarBlank, PaperPlaneTilt, TreeStructure, User } from "@phosphor-icons/react";
import { useAuth } from "../../../../../context";
import { routes } from "../../../../../config";
import { useGetOrganizationUsers, useSendTaskReminder } from "../../../../../hooks/query";
import { QUESTIONNAIRE_STATUSES, showErrorToast } from "../../../../../utils";
import { getTitle, SCOPE_COLORS, STATUS_CONFIG } from "./TaskCard";
import CcPeoplePicker from "./CcPeoplePicker";
import ReminderEmailPreview from "./ReminderEmailPreview";

const MESSAGE_LIMIT = 1000;
const NUDGE_TAIL =
  "\n\nIf anything is blocking you, or you need more time, reply to this email and let me know.";

// Same scope-tag derivation TaskCard.jsx uses for its own header chip, kept local rather than
// re-exported alongside parseScopeInfo — this modal only ever needs the FIRST tag (a task has one
// scope "home"), where the card needs the full list including the Scope 3 category suffix.
const periodLabel = (task) => `${task.year}${task.quarter && task.quarter !== "ALL" ? ` ${task.quarter}` : ""}`;

const defaultSubject = (task) => `Reminder: ${getTitle(task)} · ${periodLabel(task)}`;

// No dedicated "what data is needed" field exists on the task object — per the brief's own fallback
// rule ("If there is no specific description of the data needed, fall back to the task title"), this
// always falls back to the title, since nothing in the real data model carries that description today.
const defaultMessage = (task) =>
  `Just a quick nudge that we still need the ${getTitle(task)} data to close out the quarterly GHG inventory.${NUDGE_TAIL}`;

// `task.assigneeName` is the only identifier the task object carries for its assignee — in every real
// task this is the email (every existing chip renders it uppercase as one), so the "first name" the
// email template greets by is derived from the local part, capitalised. A genuine display name (with a
// space) is handled too, just in case.
const assigneeFirstName = (task) => {
  const raw = (task.assigneeName || "").split(/[@.\s]/)[0] || task.assigneeName || "";
  return raw ? raw[0].toUpperCase() + raw.slice(1).toLowerCase() : "";
};

/**
 * The "Send reminder" compose modal — opened from `TaskCard`'s kebab menu. Form on the left, a live
 * preview of the email on the right.
 *
 * The email itself is rendered server-side from `backend/.../templates/task-reminder-email.html`, which
 * `ReminderEmailPreview` mirrors, and with the same derived values (`TaskReminderEmail.java`): change the
 * greeting, rows or link here and change them there too, or the preview stops being what is sent.
 * `onSent` receives the stored reminder the server answered with.
 */
export default function SendReminderModal({ isOpen, task, onClose, onSent }) {
  const toast = useToast();
  const { user } = useAuth();
  const [cc, setCc] = useState([]);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [messageTouched, setMessageTouched] = useState(false);

  const { data: users } = useGetOrganizationUsers({ enabled: isOpen });
  const sendReminder = useSendTaskReminder();

  // Reset the form to this task's defaults every time the modal opens for a (possibly different) task
  // — not on every render, so the admin's edits survive re-renders while it's open.
  useEffect(() => {
    if (!isOpen || !task) return;
    setCc([]);
    setSubject(defaultSubject(task));
    setMessage(defaultMessage(task));
    setMessageTouched(false);
  }, [isOpen, task]);

  if (!task) return null;

  const scopeTag = SCOPE_COLORS[parseScopePrimary(task)];
  const title = getTitle(task);
  const period = periodLabel(task);
  const dimensionLabels = (task.dimensions || []).filter((d) => d.value !== "All").map((d) => d.value);

  const onMessageChange = (e) => {
    setMessage(e.target.value.slice(0, MESSAGE_LIMIT));
    setMessageTouched(true);
  };
  const resetMessage = () => {
    setMessage(defaultMessage(task));
    setMessageTouched(false);
  };
  const isMessageDefault = message === defaultMessage(task);

  const handleSend = () => {
    sendReminder.mutate(
      {
        id: task.id,
        data: { ccUserIds: cc.map((u) => u.id), subject, message },
      },
      {
        onSuccess: (reminder) => {
          onSent?.({ task, reminder });
          onClose();
        },
        onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
      }
    );
  };

  const senderName = user?.name || user?.email || "";
  const senderEmail = user?.email || "";

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent maxW="1300px" maxH="90vh" borderRadius="16px" overflow="hidden" display="flex" flexDirection="column">
        <Flex align="flex-start" justify="space-between" gap={4} px={7} py={5} borderBottom="1px solid" borderColor="gray.200" flexShrink={0}>
          <Box flex="1" minW={0}>
            <Text fontSize="20px" fontWeight="800" color="darkTeal">
              Send reminder
            </Text>
            <Wrap spacing={3} align="center" mt={2} fontSize="13px" color="gray.500">
              {scopeTag && (
                <WrapItem>
                  <HStack spacing={0}>
                    <Box display="inline-flex" alignItems="center" gap="4px" bg={scopeTag.light} color={scopeTag.text} fontSize="11px" fontWeight="600" px="8px" h="20px" borderRadius="full" whiteSpace="nowrap">
                      <scopeTag.icon size={12} weight="fill" />
                      {scopeTag.label}
                    </Box>
                  </HStack>
                </WrapItem>
              )}
              <WrapItem>
                <Text fontWeight="600" color="gray.700">{title}</Text>
              </WrapItem>
              <WrapItem>
                <HStack spacing={1.5}>
                  <CalendarBlank size={13} color="#A0AEC0" />
                  <Text>{period}</Text>
                </HStack>
              </WrapItem>
              {dimensionLabels.map((label, i) => (
                <WrapItem key={i}>
                  <HStack spacing={1.5}>
                    <TreeStructure size={13} color="#A0AEC0" />
                    <Text>{label}</Text>
                  </HStack>
                </WrapItem>
              ))}
            </Wrap>
          </Box>
          <ModalCloseButton position="static" />
        </Flex>

        {/* The body takes what the header and footer leave of the 90vh, and each column scrolls on its
            own inside it. Without the flex/minH chain the columns grow to their content, the modal's
            overflow="hidden" clips them, and neither ever gets a scrollbar. */}
        <ModalBody p={0} flex="1" minH={0} display="flex" overflow="hidden">
          <Flex flex="1" minH={0} w="100%">
            {/* Left column — the form */}
            <Box w="480px" flexShrink={0} overflowY="auto" px={7} py={6} borderRight="1px solid" borderColor="gray.200">
              <Flex direction="column" gap={6}>
                <Box>
                  <Flex align="baseline" justify="space-between">
                    <Text fontSize="13px" fontWeight="700">To</Text>
                    <Text fontSize="12px" color="gray.400">Task assignee, added automatically</Text>
                  </Flex>
                  <Flex mt={2} wrap="wrap" gap={2} p={2} border="1px solid" borderColor="gray.200" borderRadius="10px" bg="gray.50">
                    <Tag size="md" borderRadius="8px" bg="#FFF8E1" px={2} h="28px">
                      <User size={14} color="#718096" />
                      <Text ml={1.5} fontSize="12px" fontWeight="700" letterSpacing="0.02em" color="gray.700" textTransform="uppercase">
                        {task.assigneeName}
                      </Text>
                    </Tag>
                  </Flex>
                </Box>

                <Box>
                  <Flex align="baseline" justify="space-between">
                    <Text fontSize="13px" fontWeight="700">Cc</Text>
                    <Text fontSize="12px" color="gray.400">Optional</Text>
                  </Flex>
                  <Box mt={2}>
                    <CcPeoplePicker
                      value={cc}
                      onChange={setCc}
                      users={users}
                      excludeIds={[task.assigneeId].filter(Boolean)}
                    />
                  </Box>
                  <Text mt={1.5} fontSize="12px" color="gray.400">
                    People in copy get the same email. They aren&apos;t added to the task.
                  </Text>
                </Box>

                <Box>
                  <Text fontSize="13px" fontWeight="700" mb={2}>Subject</Text>
                  <Box
                    as="input"
                    w="100%"
                    px={3}
                    py={2.5}
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="10px"
                    fontSize="14px"
                    color="darkTeal"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    _focus={{ borderColor: "lime", boxShadow: "0 0 0 3px rgba(133,194,42,.18)", outline: "none" }}
                  />
                </Box>

                <Box>
                  <Flex align="baseline" justify="space-between" mb={2}>
                    <Text fontSize="13px" fontWeight="700">Message</Text>
                    {messageTouched && !isMessageDefault && (
                      <Text
                        as="button"
                        type="button"
                        fontSize="12px"
                        fontWeight="700"
                        color="limeShade.700"
                        onClick={resetMessage}
                      >
                        Reset to default
                      </Text>
                    )}
                  </Flex>
                  <Textarea
                    rows={8}
                    value={message}
                    onChange={onMessageChange}
                    resize="vertical"
                    fontSize="14px"
                    borderColor="gray.200"
                    borderRadius="10px"
                    _focus={{ borderColor: "lime", boxShadow: "0 0 0 3px rgba(133,194,42,.18)" }}
                  />
                  <Flex justify="space-between" mt={1.5} fontSize="12px" color="gray.400">
                    <Text>Appears after the greeting, above the task details.</Text>
                    <Text>{message.length.toLocaleString()} / {MESSAGE_LIMIT.toLocaleString()}</Text>
                  </Flex>
                </Box>
              </Flex>
            </Box>

            {/* Right column — the preview */}
            <Box flex="1" minW={0} overflowY="auto" bg="#EDF1F3">
              <Flex align="baseline" justify="space-between" px={6} pt={3.5} pb={2.5}>
                <HStack spacing={2.5} align="baseline">
                  <Text fontSize="13px" fontWeight="800" letterSpacing="0.06em" textTransform="uppercase" color="gray.600">
                    Preview
                  </Text>
                  <Text fontSize="12px" color="gray.400">Updates as you type</Text>
                </HStack>
              </Flex>

              <Box mx={6} bg="white" border="1px solid" borderColor="gray.200" borderRadius="10px" px={4} py={3}>
                <HStack align="start" spacing={2} fontSize="12.5px" mb={1.5}>
                  <Text color="gray.400" w="48px" flexShrink={0}>From</Text>
                  <Text color="gray.700">{senderName} via Antaia &lt;no-reply@antaia.io&gt;</Text>
                </HStack>
                <HStack align="start" spacing={2} fontSize="12.5px" mb={1.5}>
                  <Text color="gray.400" w="48px" flexShrink={0}>To</Text>
                  <Text color="gray.700">{task.assigneeName}</Text>
                </HStack>
                <HStack align="start" spacing={2} fontSize="12.5px" mb={1.5}>
                  <Text color="gray.400" w="48px" flexShrink={0}>Cc</Text>
                  <Text color="gray.700">
                    {cc.length > 0 ? cc.map((u) => [u.firstName, u.lastName].filter(Boolean).join(" ") || u.email).join(", ") : "—"}
                  </Text>
                </HStack>
                <HStack align="start" spacing={2} fontSize="12.5px">
                  <Text color="gray.400" w="48px" flexShrink={0}>Subject</Text>
                  <Text fontWeight="700" color="darkTeal">{subject}</Text>
                </HStack>
              </Box>

              <Box px={6} py={5}>
                <ReminderEmailPreview
                  assigneeFirstName={assigneeFirstName(task)}
                  message={message}
                  taskTitle={title}
                  rows={[
                    { label: "Data type", value: `GHG emissions · ${scopeTag?.label || ""}` },
                    { label: "Reporting period", value: period },
                    { label: "Entity", value: dimensionLabels.join(" & ") || "Company-wide" },
                    {
                      label: "Status",
                      value: (STATUS_CONFIG[task.originalStatus] || STATUS_CONFIG[QUESTIONNAIRE_STATUSES.NEW]).label,
                    },
                  ]}
                  taskLink={`${window.location.origin}${routes.organisationQuestionnaire}?id=${task.id}`}
                  senderName={senderName}
                  senderEmail={senderEmail}
                  ccNames={cc.map((u) => [u.firstName, u.lastName].filter(Boolean).join(" ") || u.email)}
                />
              </Box>
            </Box>
          </Flex>
        </ModalBody>

        <Flex align="center" justify="space-between" px={7} py={4} borderTop="1px solid" borderColor="gray.200" flexShrink={0}>
          <Text fontSize="13px" color="gray.500">
            1 assignee · {cc.length} in copy
          </Text>
          <HStack spacing={3}>
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Tooltip label={message.trim().length === 0 ? "Write a message first" : ""} isDisabled={message.trim().length > 0}>
              <Button
                variant="primary"
                leftIcon={<PaperPlaneTilt size={16} weight="fill" />}
                onClick={handleSend}
                isLoading={sendReminder.isLoading}
                isDisabled={message.trim().length === 0}
              >
                Send reminder
              </Button>
            </Tooltip>
          </HStack>
        </Flex>
      </ModalContent>
    </Modal>
  );
}

// First scope with a configuration flag set, matching the ORDER TaskCard.jsx's own chip list uses
// (Scope 1, then 2, then 3) — a task's header only ever needs its primary scope, not the full list.
function parseScopePrimary(task) {
  const configs = task.configurations || {};
  if (configs.directEmissionsShouldBeFilled) return "scope1";
  if (configs.indirectEmissionsShouldBeFilled) return "scope2";
  return "scope3";
}
